import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { currencies as allCurrencies } from '../data/mock';
import { useAuth } from '../context/AuthContext';
import {
  Heart,
  CreditCard,
  Building2,
  Check,
  Loader2,
  Gift,
  Calendar,
  Copy,
  CheckCircle,
  ChevronDown,
  ArrowRight,
  Search
} from 'lucide-react';
import { BACKEND_URL } from '../utils/config';

const SupportUsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [donationType, setDonationType] = useState('one_time');
  const [selectedAmount, setSelectedAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState('');
  const [currency, setCurrency] = useState(allCurrencies[0]);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [currencySearch, setCurrencySearch] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [showBankModal, setShowBankModal] = useState(false);
  const [referenceId, setReferenceId] = useState('');
  const [copied, setCopied] = useState('');
  
  const [formData, setFormData] = useState({
    donor_name: '',
    donor_email: '',
    message: '',
    is_anonymous: false
  });

  const presetAmounts = [10, 25, 50, 100, 250];

  useEffect(() => {
    fetchConfig();
  }, []);

  // Auto-fill user data when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData(prev => ({
        ...prev,
        donor_name: user.name || '',
        donor_email: user.email || ''
      }));
    }
  }, [isAuthenticated, user]);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/platform-donations/config`);
      const data = await res.json();
      setConfig(data);
    } catch (err) {
      console.error('Error fetching config:', err);
    }
  };

  // Filter currencies based on search
  const filteredCurrencies = allCurrencies.filter(c =>
    c.code.toLowerCase().includes(currencySearch.toLowerCase()) ||
    c.name.toLowerCase().includes(currencySearch.toLowerCase())
  );

  const getAmount = () => {
    if (customAmount && parseFloat(customAmount) > 0) {
      return parseFloat(customAmount);
    }
    return selectedAmount;
  };

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (value) => {
    setCustomAmount(value);
    if (value && parseFloat(value) > 0) {
      setSelectedAmount(null);
    }
  };

  const handleDonate = async () => {
    const amount = getAmount();
    if (!amount || amount < 1 || amount > 10000) {
      alert('Donation amount must be between $1 and $10,000');
      return;
    }
    if (!formData.donor_name || !formData.donor_email) {
      alert('Please fill in your name and email');
      return;
    }

    if (paymentMethod === 'bank') {
      handleBankTransfer();
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/platform-donations/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          currency: currency.code.toLowerCase(),
          donor_name: formData.donor_name,
          donor_email: formData.donor_email,
          message: formData.message,
          is_anonymous: formData.is_anonymous,
          donation_type: donationType,
          origin_url: window.location.origin
        })
      });

      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        alert('Failed to create payment session');
      }
    } catch (err) {
      console.error('Donation error:', err);
      alert('Failed to process donation');
    } finally {
      setLoading(false);
    }
  };

  const handleBankTransfer = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/platform-donations/bank-transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: getAmount(),
          donor_name: formData.donor_name,
          donor_email: formData.donor_email,
          donation_type: donationType,
          message: formData.message
        })
      });

      const data = await res.json();
      if (data.success) {
        setReferenceId(data.reference_id);
        setShowBankModal(true);
      }
    } catch (err) {
      console.error('Bank transfer error:', err);
      alert('Failed to register bank transfer');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(''), 2000);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/25">
              <Heart className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Support FundTheHope</h1>
            <p className="text-muted-foreground">
              Help us maintain <span className="text-emerald-400 font-semibold">0% platform fees</span> for everyone
            </p>
          </div>

          {/* Main Card - Everything in One View */}
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Left Column - Amount & Type */}
                <div className="space-y-5">
                  {/* Donation Type Toggle */}
                  <div className="flex bg-secondary rounded-xl p-1">
                    <button
                      onClick={() => setDonationType('one_time')}
                      className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                        donationType === 'one_time'
                          ? 'bg-emerald-500 text-white shadow'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Gift className="w-4 h-4" /> One-Time
                    </button>
                    <button
                      onClick={() => setDonationType('monthly')}
                      className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                        donationType === 'monthly'
                          ? 'bg-teal-500 text-white shadow'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Calendar className="w-4 h-4" /> Monthly
                    </button>
                  </div>

                  {/* Currency Selector */}
                  <div className="relative">
                    <Label className="text-xs text-muted-foreground mb-1.5 block">Currency</Label>
                    <button
                      onClick={() => { setShowCurrencyDropdown(!showCurrencyDropdown); setCurrencySearch(''); }}
                      className="w-full flex items-center justify-between px-4 py-3 bg-secondary rounded-xl border border-border hover:border-emerald-500/50 transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <span className="text-xl">{currency.flag}</span>
                        <span className="font-medium">{currency.code}</span>
                        <span className="text-muted-foreground text-sm">({currency.symbol})</span>
                      </span>
                      <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${showCurrencyDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {showCurrencyDropdown && (
                      <div className="absolute z-20 w-full mt-1 bg-card border border-border rounded-xl shadow-lg overflow-hidden">
                        {/* Search Input */}
                        <div className="p-2 border-b border-border">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <input
                              type="text"
                              placeholder="Search currency..."
                              value={currencySearch}
                              onChange={(e) => setCurrencySearch(e.target.value)}
                              className="w-full pl-9 pr-3 py-2 bg-secondary rounded-lg text-sm border-0 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                              autoFocus
                            />
                          </div>
                        </div>
                        {/* Currency List */}
                        <div className="max-h-60 overflow-y-auto">
                          {filteredCurrencies.slice(0, 50).map((cur) => (
                            <button
                              key={cur.code}
                              onClick={() => { setCurrency(cur); setShowCurrencyDropdown(false); setCurrencySearch(''); }}
                              className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-secondary transition-colors ${
                                currency.code === cur.code ? 'bg-emerald-500/10' : ''
                              }`}
                            >
                              <span className="text-xl">{cur.flag}</span>
                              <span className="font-medium">{cur.code}</span>
                              <span className="text-muted-foreground text-sm flex-1 text-left truncate">{cur.name}</span>
                              {currency.code === cur.code && <Check className="w-4 h-4 text-emerald-500" />}
                            </button>
                          ))}
                          {filteredCurrencies.length === 0 && (
                            <p className="text-center text-muted-foreground py-4 text-sm">No currencies found</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Amount Selection */}
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1.5 block">Select Amount</Label>
                    <div className="grid grid-cols-5 gap-2 mb-3">
                      {presetAmounts.map((amount) => (
                        <button
                          key={amount}
                          onClick={() => handleAmountSelect(amount)}
                          className={`py-3 rounded-xl text-center font-semibold transition-all ${
                            selectedAmount === amount && !customAmount
                              ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow'
                              : 'bg-secondary text-foreground hover:bg-secondary/80'
                          }`}
                        >
                          {currency.symbol}{amount}
                        </button>
                      ))}
                    </div>
                    {/* Custom Amount - Inline */}
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">{currency.symbol}</span>
                      <Input
                        type="number"
                        min="1"
                        placeholder="Custom amount"
                        value={customAmount}
                        onChange={(e) => handleCustomAmountChange(e.target.value)}
                        className={`pl-10 h-12 text-lg ${customAmount ? 'border-emerald-500 ring-1 ring-emerald-500/20' : ''}`}
                      />
                      {donationType === 'monthly' && (
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">/month</span>
                      )}
                    </div>
                  </div>

                  {/* Payment Method */}
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1.5 block">Payment Method</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setPaymentMethod('card')}
                        className={`p-3 rounded-xl border-2 flex items-center gap-2 transition-all ${
                          paymentMethod === 'card'
                            ? 'border-emerald-500 bg-emerald-500/10'
                            : 'border-border hover:border-emerald-500/50'
                        }`}
                      >
                        <CreditCard className="w-5 h-5 text-emerald-500" />
                        <span className="font-medium text-sm">Card</span>
                      </button>
                      <button
                        onClick={() => setPaymentMethod('bank')}
                        className={`p-3 rounded-xl border-2 flex items-center gap-2 transition-all ${
                          paymentMethod === 'bank'
                            ? 'border-teal-500 bg-teal-500/10'
                            : 'border-border hover:border-teal-500/50'
                        }`}
                      >
                        <Building2 className="w-5 h-5 text-teal-500" />
                        <span className="font-medium text-sm">Bank Transfer</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right Column - Form & Submit */}
                <div className="space-y-4">
                  {/* Donor Info */}
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 block">Full Name *</Label>
                      <Input
                        placeholder="John Doe"
                        value={formData.donor_name}
                        onChange={(e) => setFormData({...formData, donor_name: e.target.value})}
                        className="h-11"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 block">Email *</Label>
                      <Input
                        type="email"
                        placeholder="you@example.com"
                        value={formData.donor_email}
                        onChange={(e) => setFormData({...formData, donor_email: e.target.value})}
                        className="h-11"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 block">Message (Optional)</Label>
                      <Input
                        placeholder="Leave a message..."
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        className="h-11"
                      />
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_anonymous}
                        onChange={(e) => setFormData({...formData, is_anonymous: e.target.checked})}
                        className="rounded border-border w-4 h-4"
                      />
                      <span className="text-sm text-muted-foreground">Make my donation anonymous</span>
                    </label>
                  </div>

                  {/* Summary & Button */}
                  <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-xl p-4 border border-emerald-500/20">
                    <div className="text-center mb-3">
                      <p className="text-xs text-muted-foreground">
                        {donationType === 'monthly' ? 'Monthly Donation' : 'One-Time Donation'}
                      </p>
                      <p className="text-3xl font-bold text-foreground">
                        {currency.symbol}{getAmount() || 0}
                        {donationType === 'monthly' && <span className="text-base font-normal">/mo</span>}
                      </p>
                    </div>

                    <Button
                      onClick={handleDonate}
                      disabled={loading || !getAmount() || !formData.donor_name || !formData.donor_email}
                      className={`w-full py-5 text-base font-semibold ${
                        paymentMethod === 'card'
                          ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500'
                          : 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500'
                      }`}
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : paymentMethod === 'card' ? (
                        <>
                          <CreditCard className="w-5 h-5 mr-2" />
                          Donate Now
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </>
                      ) : (
                        <>
                          <Building2 className="w-5 h-5 mr-2" />
                          Get Bank Details
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-center text-muted-foreground mt-2">
                      🔒 Secure payment • Supports platform development
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Why Support Us - Compact */}
          <div className="mt-6 grid grid-cols-4 gap-3 text-center">
            {[
              { label: '0%', desc: 'Platform Fee' },
              { label: '2,500+', desc: 'Campaigns' },
              { label: '50K+', desc: 'Lives Changed' },
              { label: 'Responsive', desc: 'Support' },
            ].map((item, i) => (
              <div key={i} className="bg-card/50 rounded-xl p-3 border border-border">
                <p className="text-xl font-bold text-emerald-400">{item.label}</p>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Bank Transfer Modal */}
      {showBankModal && config?.bank_details && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-card border-border">
            <CardContent className="p-6">
              <div className="w-14 h-14 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-7 h-7 text-emerald-500" />
              </div>
              <h2 className="text-xl font-bold text-center text-foreground mb-1">Bank Transfer Details</h2>
              <p className="text-sm text-muted-foreground text-center mb-4">
                Transfer {currency.symbol}{getAmount()} to complete your donation
              </p>

              {/* Reference ID */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4">
                <p className="text-xs text-amber-400 font-medium mb-1">⚠️ Include this Reference ID</p>
                <div className="flex items-center justify-between">
                  <code className="text-lg font-mono font-bold text-foreground">{referenceId}</code>
                  <button onClick={() => copyToClipboard(referenceId, 'ref')} className="p-1">
                    {copied === 'ref' ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
                  </button>
                </div>
              </div>

              {/* Bank Details */}
              <div className="space-y-2 text-sm">
                {[
                  { label: 'Bank', value: config.bank_details.bank_name, key: 'bank' },
                  { label: 'Account Name', value: config.bank_details.account_name, key: 'name' },
                  { label: 'Account #', value: config.bank_details.account_number, key: 'acc' },
                  { label: 'Routing #', value: config.bank_details.routing_number, key: 'route' },
                  { label: 'SWIFT', value: config.bank_details.swift_code, key: 'swift' },
                ].map((item) => (
                  <div key={item.key} className="flex items-center justify-between py-1.5 border-b border-border">
                    <span className="text-muted-foreground">{item.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{item.value}</span>
                      <button onClick={() => copyToClipboard(item.value, item.key)} className="text-muted-foreground hover:text-foreground">
                        {copied === item.key ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-xs text-muted-foreground text-center mt-4">
                We'll verify and confirm within 1-3 business days
              </p>

              <Button onClick={() => navigate('/')} className="w-full mt-4">
                Done
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default SupportUsPage;
